import {
  CampaignCatalogueItem,
  FeaturedCampaignTemplatesResponse,
} from '@/client/campaigns';
import { CampaignCatalogueCardV2 } from '@/components/campaigns/CampaignCatalogueCardV2';
import RewardsIcon from '@/images/phishing/signs/rewards.svg';
import { Skeleton } from 'primereact/skeleton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CatalogueGrid } from './CatalogueGrid.styled';

const StyledFeaturedSection = styled.div`
  width: 100%;
  background-color: var(--beige-main);
  border-radius: var(--medium-border-radius);
  padding: var(--big-padding);
  margin-bottom: var(--default-padding);

  ${CatalogueGrid} {
    gap: var(--medium-padding);
  }

  ${CatalogueGrid} > * {
    background-color: var(--white-main);
  }
`;

const StyledFeaturedHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--small-padding);
  margin-bottom: var(--default-padding);

  h2 {
    font-size: var(--heading-medium-font-size);
    line-height: var(--heading-medium-line-height);
    font-weight: 700;
    color: var(--black-main);
    margin: 0;
  }

  p {
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    color: var(--black-main);
    margin: 0;
  }
`;

const StyledFeaturedIcon = styled.img`
  width: 47px;
  height: 47px;
  flex-shrink: 0;
`;

type FeaturedSimulationsSectionProps = {
  featuredTemplates: FeaturedCampaignTemplatesResponse | undefined;
  isLoading: boolean;
  mode: 'create' | 'select';
  canPerformAction: boolean;
  hideCreateAction?: boolean;
  onPreview: (campaignId: string) => void;
  onSelect: (campaignId: string) => void;
};

export const FeaturedSimulationsSection: React.FC<
  FeaturedSimulationsSectionProps
> = ({
  featuredTemplates,
  isLoading,
  mode,
  canPerformAction,
  hideCreateAction,
  onPreview,
  onSelect,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <StyledFeaturedSection>
        <CatalogueGrid>
          <Skeleton
            width="100%"
            height="350px"
            borderRadius="var(--semi-medium-border-radius)"
          />
          <Skeleton
            width="100%"
            height="350px"
            borderRadius="var(--semi-medium-border-radius)"
          />
          <Skeleton
            width="100%"
            height="350px"
            borderRadius="var(--semi-medium-border-radius)"
          />
        </CatalogueGrid>
      </StyledFeaturedSection>
    );
  }

  if (!featuredTemplates?.items?.length) {
    return null;
  }

  return (
    <StyledFeaturedSection>
      <StyledFeaturedHeader>
        <StyledFeaturedIcon src={RewardsIcon} alt="" aria-hidden="true" />
        <div>
          <h2>{t('campaigns.catalogue.featured.title')}</h2>
          <p>
            {featuredTemplates.isSeasonal
              ? t('campaigns.catalogue.featured.subtitle.seasonal')
              : t('campaigns.catalogue.featured.subtitle.default')}
          </p>
        </div>
      </StyledFeaturedHeader>

      <CatalogueGrid>
        {featuredTemplates.items.map((campaign: CampaignCatalogueItem) => (
          <CampaignCatalogueCardV2
            key={campaign.masterTemplateId ?? campaign.id}
            campaign={campaign}
            onPreview={onPreview}
            onSelect={onSelect}
            mode={mode}
            canPerformAction={canPerformAction}
            hideCreateAction={hideCreateAction}
          />
        ))}
      </CatalogueGrid>
    </StyledFeaturedSection>
  );
};
